import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Select from "react-select";
import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import { useAuth } from "../context/AuthContext";
import { CompanySelect } from "../components/CompanySelect";
import { MerchantSelect } from "../components/MerchantSelect";
import { formatCurrency, formatInteger } from "../helpers/formatNumber";
import { merchantLabel } from "../helpers/merchantLabel";
import { BusinessAccountService } from "../api/generated/services/BusinessAccountService";
import { BusinessAccountTransactionViewModel } from "../api/generated/models/BusinessAccountTransactionViewModel";
import { BusinessAccountTransactionSource } from "../api/generated/models/BusinessAccountTransactionSource";
import {
  buildAdminTransactionsQuery,
  emptyFilterDraft,
  filtersToSearchParams,
  searchParamsToFilters,
  AdminTransactionsFilterDraft,
} from "../helpers/adminTransactionsQuery";
import "../api/openApiConfig";
import TransactionDetailsModal from "../components/AdminTransactions/TransactionDetailsModal";

const PAGE_SIZE = 100;

const SOURCE_OPTIONS = Object.values(BusinessAccountTransactionSource).map(
  (value) => ({ value, label: value })
);

const AdminTransactions: React.FC = () => {
  const { selectedCompany, selectedMerchant } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // URL is the single source of truth for applied filters and page.
  const {
    filters: appliedFilters,
    page: pageNumber,
    merchant: linkedMerchant,
  } = useMemo(
    () => searchParamsToFilters(searchParams),
    [searchParams]
  );
  const [transactions, setTransactions] = useState<BusinessAccountTransactionViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  // Draft = what the user is editing; active = what has been applied and drives fetches.
  const [filters, setFilters] = useState<AdminTransactionsFilterDraft>(
    () => searchParamsToFilters(searchParams).filters
  );
  const [selectedTransaction, setSelectedTransaction] =
    useState<BusinessAccountTransactionViewModel | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [stats, setStats] = useState<{
    totalTransactionAmount: number;
    totalAssignedCashback: number;
  }>({
    totalTransactionAmount: 0,
    totalAssignedCashback: 0,
  });

  useEffect(() => {
    setFilters(appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    let ignore = false;
    const run = async () => {
      try {
        setLoading(true);
        const query = buildAdminTransactionsQuery({
          filters: appliedFilters,
          pageNumber,
          pageSize: PAGE_SIZE,
          // A merchant from a deep link overrides the global company/merchant selection.
          // The backend uses MerchantId alone as the business filter when CompanyId is empty.
          companyId: linkedMerchant ? undefined : selectedCompany?.id,
          merchantId: linkedMerchant ?? selectedMerchant?.id,
        });
        const response =
          await BusinessAccountService.postApiBusinessAccountListTransactionsForAdmin(query);
        if (ignore) return;
        setTransactions(response.data?.data ?? []);
        setTotal(response.data?.total ?? 0);
        setStats({
          totalTransactionAmount: response.stats?.totalTransactionAmount ?? 0,
          totalAssignedCashback: response.stats?.totalAssignedCashback ?? 0,
        });
        setError(null);
      } catch (err) {
        if (ignore) return;
        setError("Failed to fetch transactions");
        console.error("Error fetching transactions:", err);
      } finally {
        if (!ignore) {
          setLoading(false);
          setInitialLoad(false);
        }
      }
    };
    run();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, selectedCompany, selectedMerchant]);

  const handleApplyFilters = () => {
    setSearchParams(filtersToSearchParams(filters, 1, linkedMerchant));
  };

  const handleResetFilters = () => {
    setFilters(emptyFilterDraft);
    setSearchParams(new URLSearchParams());
  };

  const handleNextPage = () => {
    setSearchParams(filtersToSearchParams(appliedFilters, pageNumber + 1, linkedMerchant));
  };

  const handlePreviousPage = () => {
    setSearchParams(filtersToSearchParams(appliedFilters, Math.max(1, pageNumber - 1), linkedMerchant));
  };

  const handleShowAll = () => {
    setSearchParams(filtersToSearchParams(appliedFilters, 1));
  };

  // Show full page loader only on initial load
  if (initialLoad) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  const hasDetails = (t: BusinessAccountTransactionViewModel): boolean =>
    Boolean(t.visaDetails || t.storeboxDetails || t.affiliateDetails) ||
    // Partner transactions carry no details payload, but the modal still shows
    // the generic fields and the way through to the linked user.
    t.source === BusinessAccountTransactionSource.PARTNER;

  const openDetails = (t: BusinessAccountTransactionViewModel) => {
    setSelectedTransaction(t);
    setDetailsModalOpen(true);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      <div className="flex flex-col gap-4 mb-10">
        <h2 className="font-medium">
          Transactions -{" "}
          {selectedCompany
            ? selectedMerchant
              ? `${selectedCompany.name} / ${merchantLabel(selectedMerchant)}`
              : selectedCompany.name
            : "All Companies"}
        </h2>
        {linkedMerchant && (
          <div className="flex items-center gap-3 text-sm text-gray-700">
            <span>Showing one partner from a link.</span>
            <Button variant="outline" size="sm" onClick={handleShowAll}>
              Show all
            </Button>
          </div>
        )}
        <div className="flex items-center gap-4 mb-4">
          <div className="w-[300px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company
            </label>
            <CompanySelect />
          </div>
          {selectedCompany && (
            <div className="w-[300px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Merchant
              </label>
              <MerchantSelect />
            </div>
          )}
          <div className="w-[250px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date (Copenhagen)
            </label>
            <input
              type="date"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={filters.startDate}
              onChange={(e) =>
                setFilters((f) => ({ ...f, startDate: e.target.value }))
              }
            />
          </div>
          <div className="w-[250px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date (Copenhagen)
            </label>
            <input
              type="date"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={filters.endDate}
              onChange={(e) =>
                setFilters((f) => ({ ...f, endDate: e.target.value }))
              }
            />
          </div>
          <div className="w-[300px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sources
            </label>
            <Select
              isMulti
              options={SOURCE_OPTIONS}
              value={filters.sources.map((s) => ({ value: s, label: s }))}
              onChange={(selected) =>
                setFilters((f) => ({
                  ...f,
                  sources: selected.map((o) => o.value),
                }))
              }
              placeholder="All sources"
              styles={{
                control: (base) => ({
                  ...base,
                  borderColor: "#d1d5db",
                  minHeight: "2.5rem",
                  boxShadow: "none",
                  "&:hover": { borderColor: "#9ca3af" },
                }),
              }}
            />
          </div>
          <div className="w-[140px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Min Amount (kr)
            </label>
            <input
              type="number"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={filters.minAmountKr}
              onChange={(e) =>
                setFilters((f) => ({ ...f, minAmountKr: e.target.value }))
              }
            />
          </div>
          <div className="w-[140px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Amount (kr)
            </label>
            <input
              type="number"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={filters.maxAmountKr}
              onChange={(e) =>
                setFilters((f) => ({ ...f, maxAmountKr: e.target.value }))
              }
            />
          </div>
          <div className="w-[260px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Identity (email or GUID)
            </label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={filters.identity}
              onChange={(e) =>
                setFilters((f) => ({ ...f, identity: e.target.value }))
              }
            />
          </div>
        </div>
        <div className="flex items-center gap-3 mb-4">
          <Button onClick={handleApplyFilters}>Apply filters</Button>
          <Button variant="outline" onClick={handleResetFilters}>
            Reset
          </Button>
        </div>
        <div className="flex gap-4">
          <div className="bg-white rounded-lg shadow p-4 min-w-[200px] w-fit">
            <div className="text-gray-500 text-sm">Total Transactions</div>
            <div className="text-2xl font-semibold mt-1">
              {formatInteger(total)}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 min-w-[200px] w-fit">
            <div className="text-gray-500 text-sm">
              Total Transaction Amount
            </div>
            <div className="text-2xl font-semibold mt-1">
              {formatCurrency(stats.totalTransactionAmount)}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 min-w-[200px] w-fit">
            <div className="text-gray-500 text-sm">Total Assigned Cashback</div>
            <div className="text-2xl font-semibold mt-1">
              {formatCurrency(stats.totalAssignedCashback)}
            </div>
          </div>
        </div>
      </div>

      {/* Show inline loader during subsequent data fetches */}
      {loading && !initialLoad && (
        <div className="flex justify-center my-8">
          <Loader />
        </div>
      )}

      {error && <div className="error mb-4">{error}</div>}

      <div className="overflow-x-auto">
        <table className="table-default">
          <thead>
            <tr>
              <th>Merchant Name</th>
              <th className="max-w-[120px]">Transaction Amount</th>
              <th className="max-w-[120px]">Total Assigned Cashback</th>
              <th className="whitespace-nowrap">
                Cashback Assignment Time UTC
              </th>
              <th>Transaction Source</th>
              <th className="whitespace-nowrap">Identity ID</th>
              <th><span className="sr-only">Details</span></th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((transaction, index) => (
              <tr key={index}>
                <td>{transaction.merchantName}</td>
                <td className="max-w-[120px]">
                  {formatCurrency(transaction.transactionAmount ?? 0)}
                </td>
                <td className="max-w-[120px]">
                  {formatCurrency(transaction.totalAssignedCashback ?? 0)}
                </td>
                <td className="whitespace-nowrap">
                  {transaction.cashbackAssignmentTimeUTC
                    ? new Date(transaction.cashbackAssignmentTimeUTC)
                        .toLocaleString("pl", {
                          dateStyle: "short",
                          timeStyle: "short",
                          timeZone: "UTC",
                        })
                        .replace(/\./g, "/")
                    : "-"}
                </td>
                <td>{transaction.source}</td>
                <td className="font-mono text-xs whitespace-nowrap">
                  {transaction.identityId || "-"}
                </td>
                <td className="whitespace-nowrap">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!hasDetails(transaction)}
                    onClick={() => openDetails(transaction)}
                  >
                    Details
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center mt-4">
        <Button
          onClick={handlePreviousPage}
          disabled={pageNumber === 1}
          variant="outline"
        >
          Previous
        </Button>
        <span>
          Page {pageNumber} of {totalPages}
        </span>
        <Button
          onClick={handleNextPage}
          disabled={pageNumber >= totalPages}
          variant="outline"
        >
          Next
        </Button>
      </div>
      <TransactionDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        transaction={selectedTransaction}
      />
    </div>
  );
};

export default AdminTransactions;
